import { useCallback, useEffect, useRef, type AriaRole, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { DismissLayerProvider, useDismiss, type DismissReason } from '../../hooks/useDismiss';
import { useAnchoredPosition, type AnchoredWidth } from '../../hooks/useAnchoredPosition';
import { useSmoothScroll } from '../providers/SmoothScrollProvider';
import { cx, focusElementOrFirst } from './primitiveUtils';

/**
 * A floating panel attached BELOW an anchor. See Apps/web/THEME.md.
 *
 * - Portalled to <body> with fixed positioning, so the hero's `overflow-hidden` card
 *   (or any other clipping ancestor) cannot cut it off.
 * - Always opens downward. If the room below is short it scrolls the page (through
 *   Lenis on the home page) by the shortfall first.
 * - Aligns to the anchor's inline-start edge by default, so in Arabic it hangs from
 *   the right.
 * - Escape, a press outside, or Tabbing away closes it; Escape hands focus back to
 *   the anchor.
 * - `data-lenis-prevent` + `overscroll-contain`: wheel and touch scrolling inside a
 *   long list scroll the list, not the smooth-scrolled home page behind it.
 */

export interface PopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the panel hangs from; also counts as "inside" for outside-press detection. */
  anchorRef: RefObject<HTMLElement | null>;
  /** 'anchor' (default) matches the anchor; a number is px; a string is any CSS length. */
  width?: AnchoredWidth;
  /** Lower bound on the width (px number or '<n>rem'), e.g. for a narrow anchor. */
  minWidth?: number | string;
  align?: 'start' | 'end';
  /** Gap below the anchor, px. Default 8. */
  offset?: number;
  /** Room (px) wanted below the anchor before the page is scrolled. Default 240. */
  minSpaceBelow?: number;
  id?: string;
  role?: AriaRole;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  /** Focus this when the panel opens (e.g. a search box or the selected chip). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Hand focus back to the anchor when Escape closes the panel. Default true. */
  returnFocus?: boolean;
  /** Close when focus moves outside the anchor and panel. Default true. */
  closeOnFocusOutside?: boolean;
  /** Stacking order. Default 90: above the sticky header (50), the sidebar (70) and the search sheet (80). */
  zIndex?: number;
  className?: string;
  children: ReactNode;
}

export default function Popover({
  open,
  onOpenChange,
  anchorRef,
  width = 'anchor',
  minWidth,
  align = 'start',
  offset = 8,
  minSpaceBelow = 240,
  id,
  role,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  initialFocusRef,
  returnFocus = true,
  closeOnFocusOutside = true,
  zIndex = 90,
  className = '',
  children,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion() ?? false;
  const { lenis } = useSmoothScroll();

  const scrollWindowBy = useCallback(
    (amount: number, smooth: boolean) => {
      if (lenis) {
        lenis.scrollTo(window.scrollY + amount, { duration: smooth ? 0.35 : 0, immediate: !smooth });
      } else {
        window.scrollBy({ top: amount, behavior: smooth ? 'smooth' : 'auto' });
      }
    },
    [lenis],
  );

  const { style, dir, ready } = useAnchoredPosition(anchorRef, panelRef, {
    open,
    width,
    minWidth,
    align,
    offset,
    minSpaceBelow,
    scrollWindowBy,
    reducedMotion: reduce,
  });

  const onDismiss = useCallback(
    (reason: DismissReason) => {
      onOpenChange(false);
      if (reason === 'escape' && returnFocus) focusElementOrFirst(anchorRef.current);
    },
    [anchorRef, onOpenChange, returnFocus],
  );

  // Popovers opened from inside this one nest under it (see useDismiss).
  const layer = useDismiss({
    enabled: open,
    refs: [anchorRef, panelRef],
    onDismiss,
    focusOutside: closeOnFocusOutside,
  });

  // Move focus in once the panel is placed (focusing earlier would scroll to 0,0).
  useEffect(() => {
    if (open && ready && initialFocusRef?.current) {
      initialFocusRef.current.focus({ preventScroll: true });
    }
  }, [open, ready, initialFocusRef]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      ref={panelRef}
      id={id}
      role={role}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      dir={dir}
      data-popover=""
      data-lenis-prevent=""
      initial={{ opacity: 0, y: reduce ? 0 : -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduce ? 0 : 0.14, ease: [0.22, 1, 0.36, 1] }}
      style={{ ...style, zIndex }}
      className={cx(
        'overflow-y-auto overscroll-contain rounded-xl border border-[var(--pv-border-soft)]',
        'bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-text)] shadow-brand-md',
        'focus:outline-none',
        className,
      )}
    >
      <DismissLayerProvider value={layer}>{children}</DismissLayerProvider>
    </motion.div>,
    document.body,
  );
}
